import React from 'react';
import { 
  Factory, 
  ArrowRight, 
  BarChart2, 
  UserCheck, 
  Zap, 
  Target
} from 'lucide-react';
import HeroImage from '../../components/HeroImage';

function LeadGeneration() {
  const GOOGLE_FORM_URL = "https://docs.google.com/forms/d/e/1FAIpQLScVJ5NP38k3xJuhVYWnZSFm8PKrzA6Dqu7U73X9fdCI5A2PtQ/viewform?usp=header";

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen mx-[4.2mm] mt-[4.2mm]">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#00ffff]/10 via-transparent to-[#ff0000]/10 animate-gradient z-10 rounded-lg"></div>
          <div className="absolute inset-0 rounded-lg overflow-hidden">
            <HeroImage 
              src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80" 
              alt="Business data visualization"
              priority={true}
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50 rounded-lg z-20"></div>
        </div>

        <div className="relative h-full flex items-center z-30">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-center mb-8">
              <Factory className="h-16 w-16 text-[#00ffff]" />
            </div>
            <h1 className="text-5xl font-bold text-center text-white mb-6">Lead Generation</h1>
            <p className="text-xl text-white/80 text-center max-w-3xl mx-auto">
              A business is nothing without a customer base. We connect you to customers who are genuinely and intrinsically interested in your products and services.
            </p>
          </div>
        </div>
      </section>

      {/* What We Offer Section */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold text-white mb-12">Why Trust Atlas AI for Lead Generation?</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div className="white-holographic-card p-8 rounded-lg">
            <div className="mb-6">
              <BarChart2 className="h-12 w-12 text-[#00ffff]" />
            </div>
            <h3 className="text-2xl font-semibold text-gray-800 mb-6">Data-Driven Precision</h3>
            <p className="text-gray-600">
              Imagine having a tool that sorts through millions of data points to uncover your best opportunities. That's exactly what our AI does—pinpointing and qualifying leads with remarkable accuracy, so you can focus your time and energy on the prospects most likely to say "yes."
            </p>
          </div>
          
          <div className="white-holographic-card p-8 rounded-lg">
            <div className="mb-6">
              <UserCheck className="h-12 w-12 text-[#00ffff]" />
            </div>
            <h3 className="text-2xl font-semibold text-gray-800 mb-6">Personalized Approach</h3>
            <p className="text-gray-600">
              We know every business is unique. That's why we don't do cookie-cutter strategies. Instead, our AI gets to know your industry, market, and goals, creating lead-generation strategies tailored specifically to your needs.
            </p>
          </div>

          <div className="white-holographic-card p-8 rounded-lg">
            <div className="mb-6">
              <Zap className="h-12 w-12 text-[#00ffff]" />
            </div>
            <h3 className="text-2xl font-semibold text-gray-800 mb-6">Rapid Results</h3>
            <p className="text-gray-600">
              No more waiting months to see results. With our AI-powered system, you'll start seeing qualified leads roll in within days. It's a game-changer for businesses ready to grow fast.
            </p>
          </div>

          <div className="white-holographic-card p-8 rounded-lg">
            <div className="mb-6">
              <Target className="h-12 w-12 text-[#00ffff]" />
            </div>
            <h3 className="text-2xl font-semibold text-gray-800 mb-6">Precision Targeting</h3>
            <p className="text-gray-600">
              Why waste time on the wrong audience? With advanced targeting, our AI helps you connect directly with decision-makers and stakeholders who fit your ideal customer profile. It's marketing with laser focus.
            </p>
          </div>
        </div>
      </div>

      {/* Process Section */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold text-center text-white mb-16">Your Journey to Growth</h2>
        <div className="grid md:grid-cols-3 gap-8">
          <ProcessStep
            number="01"
            title="Understand"
            description="We take time to truly understand your business and who you want to connect with."
          />
          <ProcessStep
            number="02"
            title="Connect"
            description="Create meaningful connections with people who value what you offer."
          />
          <ProcessStep
            number="03"
            title="Grow"
            description="Build lasting relationships that help your business thrive."
          />
        </div>
      </div>

      {/* CTA Section */}
      <div className="max-w-7xl mx-auto px-4 py-24">
        <div className="white-holographic-card p-12 rounded-2xl text-center">
          <h2 className="text-3xl font-bold text-gray-800 mb-6">Ready to Meet Your Most Loyal Customers?</h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            Your best customers are only a click away. Let's set up the meeting!
          </p>
          <a
            href={GOOGLE_FORM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="px-8 py-4 bg-[#00ffff] text-white rounded-lg hover:bg-[#ff0000] transition-colors font-semibold inline-flex items-center mx-auto"
          >
            Let's Do It
            <ArrowRight className="ml-2 h-5 w-5" />
          </a>
        </div>
      </div>
    </div>
  );
}

function ProcessStep({ number, title, description }) {
  return (
    <div className="text-center p-6">
      <div className="text-4xl font-bold text-[#00ffff] mb-4">{number}</div>
      <h3 className="text-xl font-semibold text-white mb-2">{title}</h3>
      <p className="text-white/80">{description}</p>
    </div>
  );
}

export default LeadGeneration;